// The second factor after a sign-in without a passkey (D56): the admin console asks for a code
// from an authenticator app, within ten minutes of signing in. The first time, it sets the app
// up (a QR code and the secret, shown once; WorkOS keeps the factor, Apsio keeps neither). Five
// wrong codes in ten minutes lock it for fifteen. Only the admin console asks for this.
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, useId, useState } from 'react'
import { API_BASE } from '../api/client'
import {
  enrollTotp,
  type StepUpEnrollment,
  stepUpQuery,
  type VerifyOutcome,
  verifyTotp,
} from '../api/queries'
import { errorMessage } from '../components/states'
import { useI18n } from '../i18n'

/** The secret in groups of four, as authenticator apps print it. */
const grouped = (secret: string) => secret.replace(/(.{4})(?=.)/g, '$1 ')

export function StepUp({ onDone }: { onDone: () => Promise<void> }) {
  const { t, date } = useI18n()
  const state = useQuery(stepUpQuery)
  const [enrollment, setEnrollment] = useState<StepUpEnrollment | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [enrollFailed, setEnrollFailed] = useState<unknown>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<VerifyOutcome | null>(null)
  const codeId = useId()
  const helpId = useId()
  const at = (iso: string) => date(iso, { hour: '2-digit', minute: '2-digit' })

  async function enroll() {
    setEnrolling(true)
    setEnrollFailed(null)
    try {
      setEnrollment(await enrollTotp())
    } catch (error) {
      setEnrollFailed(error)
    } finally {
      setEnrolling(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const result = await verifyTotp(code.replace(/\s/g, ''))
      setOutcome(result)
      if (result.ok) await onDone()
      else setCode('')
    } finally {
      setBusy(false)
    }
  }

  if (state.isPending) return <p className="dim">{t('stepUp.loading')}</p>
  if (state.isError)
    return (
      <div className="stack">
        <p role="alert" className="notice error">
          {errorMessage(t, state.error)}
        </p>
        <a className="btn solid" href={`${API_BASE}/auth/sign-in`}>
          {t('signIn.continue')}
        </a>
      </div>
    )
  const lockedUntil =
    outcome && !outcome.ok && outcome.code === 'mfa_locked'
      ? outcome.lockedUntil
      : state.data.locked_until
  const expired = outcome && !outcome.ok && outcome.code === 'step_up_expired'
  const needsEnroll = state.data.state === 'enroll' && !enrollment
  const qr = enrollment?.qr_code?.startsWith('data:image/') ? enrollment.qr_code : null

  return (
    <section className="stack step-up" aria-labelledby={`${codeId}-title`}>
      <h2 id={`${codeId}-title`} style={{ fontSize: 17 }}>
        {t('stepUp.title')}
      </h2>
      <p className="dim">{needsEnroll ? t('stepUp.enrollLede') : t('stepUp.verifyLede')}</p>
      {state.data.expires_at ? (
        <p className="dim">{t('stepUp.within', { time: at(state.data.expires_at) })}</p>
      ) : null}
      {expired ? (
        <div className="stack">
          <p role="alert" className="notice error">
            {t('stepUp.expired')}
          </p>
          <a className="btn solid" href={`${API_BASE}/auth/sign-in`}>
            {t('signIn.continue')}
          </a>
        </div>
      ) : needsEnroll ? (
        <>
          <button type="button" className="btn solid" disabled={enrolling} onClick={enroll}>
            {t('stepUp.enroll')}
          </button>
          {enrollFailed ? (
            <p role="alert" className="notice error">
              {errorMessage(t, enrollFailed)}
            </p>
          ) : null}
        </>
      ) : (
        <>
          {enrollment ? (
            <div className="stack">
              <p>{t('stepUp.scan')}</p>
              {qr ? <img src={qr} alt={t('stepUp.qrAlt')} width={200} height={200} /> : null}
              <p className="dim">{t('stepUp.manual')}</p>
              <code className="mono" translate="no" data-testid="totp-secret">
                {grouped(enrollment.secret)}
              </code>
              <p className="dim">{t('stepUp.once')}</p>
            </div>
          ) : null}
          {lockedUntil ? (
            <p role="alert" className="notice error">
              {t('stepUp.locked', { time: at(lockedUntil) })}
            </p>
          ) : (
            <form className="form" onSubmit={submit} aria-label={t('stepUp.title')}>
              <div className="field">
                <label htmlFor={codeId}>{t('stepUp.code')}</label>
                <input
                  id={codeId}
                  className="input mono"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,7}"
                  maxLength={7}
                  required
                  aria-describedby={helpId}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <span id={helpId} className="hint">
                  {t('stepUp.codeHint')}
                </span>
              </div>
              {outcome && !outcome.ok && outcome.code === 'invalid_code' ? (
                <p role="alert" className="notice error">
                  {t('stepUp.wrong', { left: outcome.attemptsLeft ?? 0 })}
                </p>
              ) : outcome && !outcome.ok && outcome.code === 'failed' ? (
                <p role="alert" className="notice error">
                  {t('state.failed')}
                </p>
              ) : null}
              <button type="submit" className="btn solid" disabled={busy}>
                {t('stepUp.submit')}
              </button>
            </form>
          )}
          <p className="foot">{t('stepUp.lost')}</p>
        </>
      )}
    </section>
  )
}
